/**
 * Single source of truth for the application navigation.
 *
 * Sections group related modules under a heading. Each item is either a
 * leaf (`to`) or a group (`children`).
 *
 * Optional item fields:
 *  - roles:   staff types allowed to see the item (omit = everyone)
 *  - visible: (ctx) => boolean, for rules that go beyond the role
 *  - also:    extra route segments that should highlight this item
 *             (detail / sub pages that don't follow create-/edit- naming)
 *  - badge:   key into ctx.badges, rendered as a counter pill
 *
 * Groups with no visible children and sections with no visible items are
 * hidden automatically.
 */

export const ROLES = {
  ADMIN: "admin",
  HR: "hr",
  PM: "pm",
  STAFF: "staff",
  ACCOUNTS: "accounts",
  SUPPLY_CHAIN: "supply_chain",
  DEPOT: "depot",
  PRODUCTION: "production",
};

export const ROLE_LABELS = {
  admin: "Administrator",
  hr: "Human Resources",
  pm: "Project Manager",
  staff: "Staff",
  accounts: "Accounts",
  supply_chain: "Supply Chain",
  depot: "Depot",
  production: "Production",
};

const { ADMIN, HR, PM, STAFF, ACCOUNTS, SUPPLY_CHAIN, DEPOT, PRODUCTION } = ROLES;

export const MENU = [
  {
    id: "overview",
    items: [{ id: "dashboard", label: "Dashboard", icon: "fa-tachometer", to: "/dashboard" }],
  },
  {
    id: "administration",
    heading: "Administration",
    items: [
      {
        id: "basic-setup",
        label: "Basic Setup",
        icon: "fa-sliders",
        roles: [ADMIN, HR],
        children: [
          { id: "designation", label: "Designation", to: "/designation" },
          { id: "grade", label: "Grade", to: "/grade" },
          { id: "department", label: "Department", to: "/department" },
          { id: "companylocation", label: "Location", to: "/companylocation" },
          { id: "factory", label: "Factory", to: "/factory" },
          { id: "leave-rules", label: "Leave Rules", to: "/leave-rules" },
          { id: "holiday-list", label: "Holiday List", to: "/holiday-list" },
          { id: "roaster-holiday-list", label: "Roaster Holiday List", to: "/roaster-holiday-list" },
          { id: "weekly-off-day", label: "Weekly Off Day", to: "/weekly-off-day" },
          { id: "setting", label: "Settings", to: "/setting" },
          { id: "special-late-facility", label: "Special Late Facility", to: "/special-late-facility" },
        ],
      },
      {
        id: "workplace",
        label: "Workplace",
        icon: "fa-map-marker",
        roles: [ADMIN, HR],
        children: [
          { id: "region", label: "Region", to: "/region" },
          { id: "area", label: "Area", to: "/area" },
          { id: "base", label: "Base", to: "/base" },
          { id: "route", label: "Route", to: "/route" },
        ],
      },
    ],
  },
  {
    id: "people",
    heading: "People",
    items: [
      {
        id: "staff-management",
        label: "Staff Management",
        icon: "fa-users",
        roles: [ADMIN, HR, STAFF],
        children: [
          {
            id: "staff",
            label: "Staff",
            to: "/staff",
            roles: [ADMIN, HR],
            also: ["appointment-letter", "idcard", "profile"],
          },
          {
            id: "requisition",
            label: "Requisition",
            to: "/requisition",
            visible: (ctx) => [ADMIN, HR].includes(ctx.role) || ctx.isDepartmentHead,
          },
          { id: "candidate", label: "Candidate", to: "/candidate", roles: [ADMIN, HR] },
          { id: "leave-deduction", label: "Leave Deduction", to: "/leave-deduction", roles: [ADMIN, HR] },
          { id: "leave-application", label: "Leave Application", to: "/leave-application" },
          { id: "bill-submission", label: "Bill Submission", to: "/billsubmission", also: ["bill-submission"] },
          { id: "movement-slip", label: "Movement Slip", to: "/movement-slip" },
          { id: "attendance", label: "Attendance", to: "/attendance", also: ["import-attendance"] },
          { id: "notice", label: "Notice", to: "/notice" },
        ],
      },
      {
        id: "payroll",
        label: "Payroll",
        icon: "fa-money",
        roles: [ADMIN, HR],
        children: [
          { id: "arrear", label: "Arrear", to: "/arrear" },
          { id: "incentive", label: "Incentive", to: "/incentive" },
          { id: "overtime", label: "Overtime", to: "/overtime" },
          { id: "holiday", label: "Holiday", to: "/holiday" },
          { id: "night-hold", label: "Night Hold", to: "/night-hold" },
          { id: "tada", label: "TA/DA", to: "/tada" },
          { id: "salary-advance", label: "Salary Advance", to: "/salary-advance" },
          { id: "deduction", label: "Deduction", to: "/deduction" },
          { id: "canteen", label: "Canteen", to: "/canteen" },
          { id: "phonebill", label: "Phone Bill", to: "/phonebill" },
          { id: "incometax", label: "Income Tax", to: "/incometax" },
          { id: "bonus", label: "Bonus", to: "/bonous" },
          { id: "salary", label: "Salary", to: "/salary", also: ["pay-slip"] },
          { id: "apa", label: "APA", to: "/apa" },
        ],
      },
      {
        id: "project-management",
        label: "Projects",
        icon: "fa-tasks",
        roles: [ADMIN, HR, PM],
        children: [
          { id: "list-project", label: "Projects", to: "/list-project" },
          { id: "project-board", label: "Project Board", to: "/list-project-board" },
          { id: "backlog", label: "Backlog", to: "/backlog" },
          { id: "sprint-list", label: "Sprints", to: "/sprint-list" },
          { id: "sprint-planning", label: "Sprint Planning", to: "/sprint-planning" },
          { id: "list-story", label: "Stories", to: "/list-story" },
          { id: "list-task", label: "Tasks", to: "/list-task" },
          { id: "list-type-task", label: "Task Types", to: "/list-type-task" },
          { id: "list-label", label: "Labels", to: "/list-label" },
          { id: "task-calendar", label: "Task Calendar", to: "/task-calendar" },
          { id: "developer-task-history", label: "Developer Task History", to: "/developer-task-history" },
        ],
      },
    ],
  },
  {
    id: "finance",
    heading: "Finance",
    items: [
      {
        id: "accounts",
        label: "Accounts",
        icon: "fa-calculator",
        roles: [ADMIN, ACCOUNTS],
        children: [
          // { id: "accounts-dashboard", label: "Accounts Dashboard", to: "/accounts-dashboard" },
          { id: "budget-plan", label: "Budget Planning", to: "/budget-plan" },
          { id: "create-chart-of-account", label: "New Chart of Account", to: "/create-chart-of-account" },
          { id: "chart-of-account", label: "Chart of Accounts", to: "/chart-of-account", also: ["tree-chart-of-account"] },
          { id: "journal-code", label: "Journal Code", to: "/journal-code" },
          { id: "sub-category", label: "Sub Category", to: "/sub-category" },
          { id: "acc-customer", label: "Customer", to: "/acc-customer", also: ["create-acc-customer", "edit-acc-customer", "view-acc-customer"] },
          { id: "cost-center", label: "Cost Center", to: "/cost-center", also: ["create-cost-center", "edit-cost-center"] },
          { id: "acc-project", label: "Project", to: "/acc-project", also: ["create-acc-project", "edit-acc-project"] },
          { id: "create-journal-voucher", label: "New Journal Voucher", to: "/create-journal-voucher" },
          { id: "journal-voucher", label: "Journal Vouchers", to: "/journal-voucher", also: ["journal-voucher-details"] },
          { id: "cash-count", label: "Petty Cash", to: "/cash-count" },
          // { id: "auto-journal-setup", label: "Setup", to: "/auto-journal-setup", also: ["setup"] },
          // { id: "transactions-list-connect", label: "Transactions List Connect", to: "/transactions-list-connect" },
        ],
      },
    ],
  },
  {
    id: "operations",
    heading: "Operations",
    items: [
      {
        id: "supply-chain",
        label: "Supply Chain",
        icon: "fa-truck",
        roles: [ADMIN, SUPPLY_CHAIN, DEPOT],
        children: [
          { id: "vendors", label: "Vendors", to: "/vendors", roles: [ADMIN, SUPPLY_CHAIN], also: ["vendor"] },
          { id: "distributors", label: "Distributors", to: "/distributors", roles: [ADMIN, SUPPLY_CHAIN] },
          { id: "unit", label: "Unit", to: "/unit", roles: [ADMIN, SUPPLY_CHAIN] },
          { id: "product-category", label: "Product Category", to: "/product-category", roles: [ADMIN, SUPPLY_CHAIN] },
          { id: "product", label: "Product", to: "/product", roles: [ADMIN, SUPPLY_CHAIN] },
          { id: "direct-purchase", label: "Direct Purchase", to: "/direct-purchase" },
          {
            id: "circular-rfq",
            label: "Circular RFQ",
            to: "/circular-rfq",
            also: ["vendor-quotation-list", "vendor-quotation", "vendor-quotation-sent", "circular-compare"],
          },
          { id: "final-quotation", label: "Final Quotation", to: "/final-quotation" },
          { id: "purchase-order", label: "Purchase Order", to: "/purchase-order" },
          { id: "grn", label: "GRN", to: "/grn" },
          { id: "purchase-order-return", label: "Purchase Order Return", to: "/purchase-order-return" },
          { id: "others-receive", label: "Others Receive", to: "/others-receive" },
          { id: "trade-offer", label: "Trade Offer", to: "/trade-offer" },
        ],
      },
      {
        id: "production",
        label: "Production",
        icon: "fa-industry",
        roles: [ADMIN, SUPPLY_CHAIN, PRODUCTION, DEPOT],
        children: [
          { id: "receipe", label: "Recipe", to: "/receipe", roles: [ADMIN, SUPPLY_CHAIN], also: ["receipe-factory"] },
          { id: "requisition-production", label: "Requisition", to: "/requisition-production" },
          { id: "issue", label: "Issue", to: "/issue", also: ["rmpm-receive"] },
          { id: "production", label: "Production", to: "/production", also: ["production-generate", "production-receive"] },
          { id: "factory-transfer", label: "Factory Transfer", to: "/factory-transfer", also: ["depo-transfer", "transfer"] },
          { id: "damage-return", label: "Damage Return", to: "/damage-return" },
        ],
      },
      {
        id: "assets",
        label: "Assets",
        icon: "fa-cubes",
        roles: [ADMIN, SUPPLY_CHAIN],
        children: [
          { id: "asset-category", label: "Asset Category", to: "/asset-category" },
          { id: "asset", label: "Asset", to: "/asset" },
          { id: "asset-direct-purchase", label: "Direct Purchase", to: "/asset-direct-purchase" },
          {
            id: "asset-circular-rfq",
            label: "Circular RFQ",
            to: "/asset-circular-rfq",
            also: ["asset-vendor-quotation-list", "vendor-asset-quotation-sent", "asset-circular-compare"],
          },
          { id: "asset-final-quotation", label: "Final Quotation", to: "/asset-final-quotation" },
          { id: "asset-purchase-order", label: "Purchase Order", to: "/asset-purchase-order" },
          { id: "asset-grn", label: "Purchase Order Receive", to: "/asset-grn", also: ["asset-create-grn"] },
          { id: "asset-purchase-order-return", label: "Purchase Order Return", to: "/asset-purchase-order-return" },
          { id: "asset-assign", label: "Assign", to: "/asset-assign", also: ["return-asset-assign"] },
          {
            id: "asset-maintenence-claim",
            label: "Maintenance Claim",
            to: "/asset-maintenence-claim",
            also: ["damage-asset-claim", "asset-damage-claim"],
          },
        ],
      },
    ],
  },
  {
    id: "commercial",
    heading: "Sales & Customers",
    items: [
      {
        id: "sales",
        label: "Sales",
        icon: "fa-shopping-cart",
        roles: [ADMIN, DEPOT, SUPPLY_CHAIN, PRODUCTION],
        children: [
          { id: "primary-sales", label: "Primary Sales", to: "/primary-sales", roles: [ADMIN] },
          {
            id: "demand-sheet",
            label: "Demand Sheet",
            to: "/demand-sheet",
            also: ["demand-sheet-receipt", "demand-remarks", "demand-wise-order", "demand-generate-order"],
          },
          {
            id: "demand-forecast",
            label: "Demand Forecasting",
            to: "/demand-forecast",
            roles: [ADMIN, SUPPLY_CHAIN, PRODUCTION],
            also: ["demand-forecast-view"],
          },
        ],
      },
      {
        id: "secondary-sales",
        label: "Secondary Sales",
        icon: "fa-line-chart",
        roles: [ADMIN],
        children: [
          { id: "monthly-target", label: "Monthly Target", to: "/monthly-target" },
          { id: "create-target", label: "Create Target", to: "/create-target" },
          { id: "monthly-target-view", label: "View Monthly Target", to: "/monthly-target-view" },
          { id: "summary-report", label: "Summary Report", to: "/summary-report" },
          { id: "daily-report", label: "Daily Report", to: "/daily-report" },
          { id: "create-opening-stock", label: "Create Distributor OS", to: "/create-opening-stock" },
          { id: "opening-stock-view", label: "View Distributor OS", to: "/opening-stock-view", also: ["opening-stock"] },
        ],
      },
      {
        id: "crm",
        label: "CRM",
        icon: "fa-handshake-o",
        roles: [ADMIN, SUPPLY_CHAIN, DEPOT],
        badge: "crmPending",
        children: [
          { id: "crm-tickets", label: "Tickets", to: "/crm", badge: "crmPending" },
          { id: "merge-invoice", label: "Merge Invoices", to: "/merge-invoice" },
        ],
      },
    ],
  },
  {
    id: "insights",
    heading: "Insights",
    items: [
      {
        id: "reports",
        label: "Reports",
        icon: "fa-bar-chart",
        to: "/reports",
        visible: (ctx) => ctx.role !== HR,
      },
    ],
  },
];
